import type { MouseEvent } from 'react';
import { useState } from 'react';
import Chevron from '@/assets/icons/ChevronDown.svg';
import { Checkbox } from '@/components/Checkbox';
import * as Filter from '@/components/FilterPanel/FilterPanel.styled';
import type { CategoryFacet, CategoryFacetGroup } from '@/types';

// Match the SearchApp FilterPanel: show a handful of facets per group with a
// "Show more" toggle (groups like Author can have 100+ values).
const VISIBLE_LIMIT = 6;

// FacetType.Url (2) — sub-category navigation links
const FACET_TYPE_URL = 2;

const preventSelfNavigation = (url: string | null | undefined) => (e: MouseEvent) => {
  if (url && new URL(url, window.location.origin).pathname === window.location.pathname) {
    e.preventDefault();
  }
};

interface FacetGroupProps {
  group: CategoryFacetGroup;
  onToggle: (groupName: string, key: string) => void;
  selectedKeys?: string[];
}

const CategoryChildren = ({ children }: { children: CategoryFacet[] }) => {
  const [expanded, setExpanded] = useState(false);
  const canToggle = children.length > VISIBLE_LIMIT;
  const visible = expanded ? children : children.slice(0, VISIBLE_LIMIT);

  return (
    <>
      <ul>
        {visible.map(child => (
          <li key={child.key ?? child.name}>
            <Filter.CategoryLink
              $isRefined={child.selected}
              href={child.url ?? '#'}
              onClick={preventSelfNavigation(child.url)}
            >
              {child.name}
            </Filter.CategoryLink>
          </li>
        ))}
      </ul>
      {canToggle ? (
        <Filter.ShowMoreButton
          $isExpanded={expanded}
          onClick={() => setExpanded(v => !v)}
          type="button"
        >
          {expanded ? 'Show less' : 'Show more'}
          <Chevron />
        </Filter.ShowMoreButton>
      ) : null}
    </>
  );
};

const CategoryLinks = ({ facets }: { facets: CategoryFacet[] }) => (
  <ul>
    {facets.map(facet => (
      <li key={facet.key ?? facet.name}>
        <Filter.CategoryLink
          $isRefined={facet.selected}
          href={facet.url ?? '#'}
          onClick={preventSelfNavigation(facet.url)}
        >
          {facet.name}
        </Filter.CategoryLink>
        {facet.children && facet.children.length > 0 ? (
          <CategoryChildren children={facet.children} />
        ) : null}
      </li>
    ))}
  </ul>
);

export const FacetGroup = ({ group, onToggle, selectedKeys = [] }: FacetGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  if (group.facets.length === 0) {
    return null;
  }

  if (group.facetType === FACET_TYPE_URL) {
    return (
      <Filter.FacetSection>
        <h2>{group.groupLabel}</h2>
        {group.parentPage ? (
          <Filter.ParentPageLink
            $isRefined={false}
            href={group.parentPage.url}
            onClick={preventSelfNavigation(group.parentPage.url)}
          >
            ← {group.parentPage.name}
          </Filter.ParentPageLink>
        ) : null}
        <CategoryLinks facets={group.facets} />
      </Filter.FacetSection>
    );
  }

  const visibleFacets = expanded
    ? group.facets
    : group.facets.slice(0, VISIBLE_LIMIT);
  const canToggleShowMore = group.facets.length > VISIBLE_LIMIT;

  return (
    <Filter.FacetSection>
      <h2>{group.groupLabel}</h2>
      <ul>
        {visibleFacets.map(facet => (
          <li key={facet.key}>
            <Checkbox
              isSelected={selectedKeys.includes(facet.key)}
              // Param key is GroupName, not GroupFieldName: the server-side
              // FilterOptionFormModelBinder recognises facet groups by GroupName
              // (via FacetGroups.IsBelong) — e.g. PriceRange, not DisplayPrice.Amount.
              onChange={() => onToggle(group.groupName, facet.key)}
              label={
                <>
                  <span>{facet.name}</span>
                  {group.showFacetCount ? (
                    <Filter.FacetCount>({facet.count})</Filter.FacetCount>
                  ) : null}
                </>
              }
            />
          </li>
        ))}
      </ul>
      {canToggleShowMore ? (
        <Filter.ShowMoreButton
          $isExpanded={expanded}
          onClick={() => setExpanded(value => !value)}
          type="button"
        >
          {expanded ? 'Show less' : 'Show more'}
          <Chevron />
        </Filter.ShowMoreButton>
      ) : null}
    </Filter.FacetSection>
  );
};
